import { Analytics } from '@util/Analytics';
import { WebAnalyticsEvent, WebAnalyticsEventName } from 'hydra-data-types';
import { isEqual } from 'lodash';
import { getUtmTermSearchParam } from '../config/AssessmentUtils';
import { StoreStateInternal, store } from '../store/Store';
import { PricingSelector } from '../store/pricing/PricingActions';
import { Price } from './Currency';
import { Environment } from './Environment';

type PaymentEvents =
  | WebAnalyticsEventName.o_payment_opened
  | WebAnalyticsEventName.o_payment_started;

type PaymentEventProperties = Omit<Extract<WebAnalyticsEvent, { event: PaymentEvents }>, 'event'>;

export function trackPaymentAnalyticsEvent(
  event: PaymentEvents,
  state: StoreStateInternal = store.getState()
): void {
  const { purchase, pricing } = state;
  const planPrices = PricingSelector.prices(state);
  if (!planPrices) {
    console.error(
      'No plan prices found while tracking payment analytics event',
      event,
      state.pricing,
      state.purchase
    );
    return;
  }

  const properties: PaymentEventProperties = {
    purchaseType: planPrices.isRecurring ? 'subscription' : 'one-off',
    product: Environment.APP,
    plan: purchase.plan,
    trialPlan: purchase.trialPlan,
    trialPeriodDays: purchase.trialPeriodDays,
    addonPlan: purchase.addonPlan ?? null,
    currency: pricing.currency,
    price: Price.inMainUnit(planPrices.initialPlanPrice.value),
    utmTerm: getUtmTermSearchParam() ?? null,
  };

  // It's really hard to prevent duplicate calls to this function so we dedupe here
  dedupedAnalyticsTrack(event, properties);
}

let prevEventProperties: { [event in PaymentEvents]?: PaymentEventProperties } = {};

/** Used to prevent duplicate analytics events being sent. */
const dedupedAnalyticsTrack = <E extends PaymentEvents>(
  event: E,
  properties: PaymentEventProperties
): void => {
  const prevProperties = prevEventProperties[event];

  if (prevProperties && isEqual(prevProperties, properties)) {
    console.info('Duplicate analytics event blocked', event, properties);
    return; // Prevent duplicate events
  }

  prevEventProperties[event] = properties;
  Analytics.track({ event, ...properties } as Extract<WebAnalyticsEvent, { event: E }>);
};

/** Reset deduplication state. Exported for testing purposes. */
export function testOnly_resetDeduplicationState(): void {
  prevEventProperties = {};
}
